import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import DOMPurify from 'dompurify';
import '../styles/article-content.css';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Calendar, Clock, ArrowLeft, ArrowRight, Heart, Lightbulb, Sparkles, Target, Users, Award, TrendingUp, Zap, BookOpen, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL || 'https://learnwithzeenat.com';

function TeacherTipArticle() {
  const { article_id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likeCount, setLikeCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentName, setCommentName] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const categoryIcons = {
    'classroom_ideas': Lightbulb,
    'assessment': Target,
    'differentiation': Users,
    'ai_for_teachers': Sparkles,
    'parent_tips': Heart,
    'behaviour_management': Award,
    'student_motivation': TrendingUp,
    'inclusive_education': Users,
    'creative_teaching': Zap,
    'teacher_productivity': BookOpen
  };

  useEffect(() => {
    fetchArticle();
    fetchLikes();
    fetchComments();
    
    // Check if user has liked before (from localStorage)
    const liked = localStorage.getItem(`liked_${article_id}`);
    if (liked) {
      setHasLiked(true);
    }
    
    window.scrollTo(0, 0);
  }, [article_id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/teaching-tips/${article_id}`);
      setArticle(response.data);
      
      // Fetch related articles
      const relatedResponse = await axios.get(`${API}/teaching-tips?category=${response.data.category}`);
      const related = relatedResponse.data.filter(a => a.article_id !== article_id).slice(0, 3);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('Article not found');
      navigate('/teaching-tips');
    } finally {
      setLoading(false);
    }
  };

  const fetchLikes = async () => {
    try {
      const response = await axios.get(`${API}/teaching-tips/${article_id}/likes`);
      setLikeCount(response.data.like_count || 0);
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/teaching-tips/${article_id}/comments`);
      setComments(response.data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleLike = async () => {
    if (hasLiked) {
      toast.info('You already liked this article!');
      return;
    }

    try {
      const response = await axios.post(`${API}/teaching-tips/${article_id}/like`);
      setLikeCount(response.data.like_count);
      setHasLiked(true);
      localStorage.setItem(`liked_${article_id}`, 'true');
      toast.success('Thanks for liking this article! ❤️');
    } catch (error) {
      console.error('Error liking article:', error);
      toast.error('Failed to like article');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!commentName.trim() || commentName.trim().length < 2) {
      toast.error('Please enter your name (at least 2 characters)');
      return;
    }
    
    if (!commentText.trim() || commentText.trim().length < 3) {
      toast.error('Please enter a comment (at least 3 characters)');
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/teaching-tips/${article_id}/comments`, {
        name: commentName.trim(),
        comment: commentText.trim()
      });
      
      toast.success('Comment posted successfully!');
      setCommentName('');
      setCommentText('');
      
      // Refresh comments
      await fetchComments();
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const shareOnWhatsApp = () => {
    const url = window.location.href;
    const text = `Check out this article: ${article.title}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
  };

  const shareOnFacebook = () => {
    const url = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const url = window.location.href;
    const text = `Check out this article: ${article.title}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return null;
  }

  const CategoryIcon = categoryIcons[article.category] || Lightbulb;

  // Generate SEO-friendly description
  const metaDescription = article.excerpt || article.content.replace(/<[^>]*>/g, '').substring(0, 155) + '...';
  
  // Generate page title
  const pageTitle = `${article.title} | Learn with Zeenat`;
  
  // Current page URL for Open Graph
  const currentUrl = `${SITE_URL}/teaching-tips/${article.article_id}`;
  
  // Featured image for Open Graph (use a default if article doesn't have one)
  const ogImage = article.featured_image || `${SITE_URL}/og-default-teaching-tips.png`;

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="Learn with Zeenat" />
        <meta property="article:author" content={article.author || 'Zeenat Devjani'} />
        <meta property="article:published_time" content={article.published_at} />
        {article.subject && <meta property="article:tag" content={article.subject} />}
        {article.category && <meta property="article:section" content={formatCategory(article.category)} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* WhatsApp Optimization */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={article.title} />
        
        {/* Additional SEO */}
        <meta name="author" content={article.author || 'Zeenat Devjani'} />
        {article.keywords && <meta name="keywords" content={article.keywords} />}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
      </Helmet>

      <Navigation />
      
      {/* Article Header */}
      <article className="py-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/teacher-tips')}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Articles
          </button>

          {/* Category & Meta */}
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-4 py-2 rounded-lg inline-flex items-center gap-2">
              <CategoryIcon className="w-4 h-4" />
              {formatCategory(article.category)}
            </span>
            <span className="text-sm text-slate-500 inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {article.reading_time} min read
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            {article.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            {article.excerpt}
          </p>

          {/* Author & Date */}
          <div className="flex items-center justify-between pb-8 border-b border-slate-200 mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">Z</span>
              </div>
              <div>
                <p className="text-base font-semibold text-slate-900">{article.author}</p>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(article.published_at)}
                </div>
              </div>
            </div>
            
            {/* Social Share & Like Buttons */}
            <div className="flex items-center gap-3">
              {/* Like Button */}
              <button
                onClick={handleLike}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  hasLiked 
                    ? 'bg-red-50 text-red-600 border-2 border-red-200' 
                    : 'bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-600 border-2 border-slate-200 hover:border-red-200'
                }`}
              >
                <Heart className={`w-5 h-5 ${hasLiked ? 'fill-red-600' : ''}`} />
                <span className="font-semibold">{likeCount}</span>
              </button>

              {/* WhatsApp Share */}
              <button
                onClick={shareOnWhatsApp}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg font-medium transition-all border-2 border-green-200"
                title="Share on WhatsApp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span className="hidden sm:inline">WhatsApp</span>
              </button>

              {/* Facebook Share */}
              <button
                onClick={shareOnFacebook}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-medium transition-all border-2 border-blue-200"
                title="Share on Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="hidden sm:inline">Facebook</span>
              </button>

              {/* X (Twitter) Share */}
              <button
                onClick={shareOnTwitter}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg font-medium transition-all border-2 border-slate-900"
                title="Share on X (Twitter)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="hidden sm:inline">X (Twitter)</span>
              </button>
            </div>
          </div>

          {/* Featured Image */}
          {article.featured_image && (
            <div className="mb-12 rounded-2xl overflow-hidden shadow-lg">
              <img
                src={article.featured_image}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Article Content */}
          <div 
            className="article-content prose prose-lg prose-slate max-w-none mb-12"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
          />

          {/* Subject & Grade Tags */}
          {(article.subject || article.grade_range) && (
            <div className="flex flex-wrap gap-3 pt-8 border-t border-slate-200 mb-12">
              {article.subject && (
                <span className="text-sm text-slate-600 bg-slate-100 px-4 py-2 rounded-lg">
                  Subject: {article.subject}
                </span>
              )}
              {article.grade_range && (
                <span className="text-sm text-slate-600 bg-slate-100 px-4 py-2 rounded-lg">
                  Grades: {article.grade_range}
                </span>
              )}
            </div>
          )}

          {/* Comments Section */}
          <div className="pt-12 border-t-2 border-slate-200">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-blue-600" />
              Comments ({comments.length})
            </h2>

            {/* Comment Form */}
            <div className="bg-slate-50 rounded-2xl p-8 mb-10 border-2 border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Leave a Comment</h3>
              <p className="text-sm text-slate-600 mb-6">Share your thoughts! No account needed.</p>
              <form onSubmit={handleSubmitComment} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={commentName}
                    onChange={(e) => setCommentName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="comment" className="block text-sm font-semibold text-slate-700 mb-2">
                    Your Comment *
                  </label>
                  <textarea
                    id="comment"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="What did you think about this article?"
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors resize-none"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submittingComment}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg active:scale-95"
                >
                  <Send className="w-5 h-5" />
                  {submittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </form>
            </div>

            {/* Comments List */}
            <div className="space-y-6">
              {comments.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
                  <MessageCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-lg text-slate-600 font-medium">No comments yet</p>
                  <p className="text-sm text-slate-500 mt-2">Be the first to share your thoughts!</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.comment_id} className="bg-white rounded-xl p-6 border-2 border-slate-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-semibold text-lg">
                          {comment.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-bold text-slate-900">{comment.name}</p>
                          <span className="text-sm text-slate-500">{formatCommentDate(comment.created_at)}</span>
                        </div>
                        <p className="text-slate-700 leading-relaxed">{comment.comment}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 px-6 sm:px-8 lg:px-12 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles.map((related) => {
                const RelatedIcon = categoryIcons[related.category] || Lightbulb;
                return (
                  <Link
                    key={related.article_id}
                    to={`/teacher-tips/${related.article_id}`}
                    className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
                  >
                    {related.featured_image ? (
                      <div className="aspect-video overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50">
                        <img 
                          src={related.featured_image} 
                          alt={related.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
                        <RelatedIcon className="w-16 h-16 text-blue-400 opacity-50" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg inline-flex items-center gap-1">
                          <RelatedIcon className="w-3 h-3" />
                          {formatCategory(related.category)}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {related.excerpt}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-12 text-center">
            <Sparkles className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Want More Teacher Tips?
            </h3>
            <p className="text-base text-slate-600 mb-6 max-w-md mx-auto">
              Explore classroom-tested resources and practical teaching strategies
            </p>
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-xl px-8 py-3 font-semibold shadow-sm hover:bg-blue-700 hover:shadow-md transition-all active:scale-95"
            >
              Browse Resources
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default TeacherTipArticle;
